import useTransactionsApi from '../api/useTransactionsApi';
import { useApiErrorSnackbar } from '../common/useApiErrorSnackbar';
import { useTransactionHistory } from './useTransactionHistory';
import { useCheckRequest } from './useCheckRequest';
import {
  type TransactionActionType,
  type FetchError,
  type UseTransactionDetailsDeps
} from '@/types';
import { useTransactionsStore } from '#imports';
import { TransactionActionStatus } from '~/enums/transactions/transaction-action-status.enum';
import { TransactionMultiStepWorkflowstatus } from '~/enums/transactions/transaction-status.enum';

// Related-transaction loading is injected to keep this composable decoupled from useRelatedTransactions.
export const useTransactionDetails = ({
  loadRelatedTransactions,
  loadP2PRelatedTransactions
}: UseTransactionDetailsDeps) => {
  const { $i18n } = useNuxtApp();
  const { t } = $i18n;
  const route = useRoute();
  const router = useRouter();
  const { getInOutTransactionDetails, getP2PTransactionDetails, getTransactionRibInfos } =
    useTransactionsApi();
  const { loadTransactionHistory } = useTransactionHistory();
  const { checkMoneyOutValidationConfig } = useCheckRequest();
  const { notifyApiError } = useApiErrorSnackbar();
  const {
    transactionInfos,
    transactionP2PInfos,
    transactionActions,
    pspDetails,
    cardDetails,
    bnplDetails,
    bankAccountInfos,
    parentTransaction,
    relatedTransactions,
    relatedTransactionsCount,
    activeTab,
    history
  } = storeToRefs(useTransactionsStore());

  const transactionId = computed(() =>
    Array.isArray(route.params.id) ? route.params.id[0] : route.params.id
  );

  const isBankTransferMoneyOut = computed(
    () => transactionInfos.value?.category === 1 && transactionInfos.value?.paymentMethod === 3
  );

  const isPspMethod = computed(() => !!transactionInfos.value?.psp);

  const transactionDetailsTabs = computed(() => {
    return [
      {
        label: t('common.details'),
        value: 'details',
        permission: true
      },
      {
        label: `${t('transactions.details.tabs.relatedTransactions')} (${relatedTransactionsCount.value || 0})`,
        value: 'related-transactions',
        disabled: relatedTransactionsCount.value === 0,
        permission: true
      }
    ];
  });

  const resetTransactionInfos = () => {
    activeTab.value = 'details';
    parentTransaction.value = undefined;
    relatedTransactions.value = undefined;
    transactionInfos.value = undefined;
    transactionP2PInfos.value = undefined;
    relatedTransactionsCount.value = 0;
  };

  const getTransactionBankAccountInfos = async () => {
    if (transactionInfos.value?.rib) {
      const bankAccountResponse = await getTransactionRibInfos(transactionInfos.value.rib);
      bankAccountInfos.value = bankAccountResponse;
    }
  };

  const handleTransactionActionStatus = (key: string, status: number): number => {
    if (key === 'ValidateWireTransfer' && status === TransactionActionStatus.Allowed) {
      const userId = Number(localStorage.getItem('userId'));
      return history.value.some(
        (step) =>
          step.UserId === userId &&
          step?.ActionResult &&
          [
            TransactionMultiStepWorkflowstatus.Approved,
            TransactionMultiStepWorkflowstatus.Refused
          ].includes(step.ActionResult)
      )
        ? TransactionActionStatus.Disabled
        : status;
    }

    return status;
  };

  const getTransactionInfos = async () => {
    try {
      resetTransactionInfos();
      const data = await getInOutTransactionDetails(transactionId.value);
      transactionInfos.value = data.MoneyTransaction;
      pspDetails.value = data.PspDetail;
      cardDetails.value = data.CardDetail;
      bnplDetails.value = data.BnplDetail;
      getTransactionBankAccountInfos();
      if (isBankTransferMoneyOut.value && checkMoneyOutValidationConfig()) {
        await loadTransactionHistory();
      }
      transactionActions.value = Object.keys(data.Actions).map((key) => {
        const actionKey = key as keyof typeof data.Actions;
        const status = handleTransactionActionStatus(actionKey, data.Actions[actionKey]);
        return {
          name: key,
          status
        } as TransactionActionType;
      });
      await loadRelatedTransactions();
    } catch (error: unknown) {
      const responseStatus = (error as FetchError)?.response?.status;
      if (responseStatus === 404) {
        router.replace({
          name: 'partner-transactions-all',
          params: { partner: route.params.partner }
        });
      }
      notifyApiError(error, { title: t('common.transactionDetailsNotFound') });
    }
  };

  const getP2PTransactionInfos = async () => {
    try {
      resetTransactionInfos();
      const data = await getP2PTransactionDetails(transactionId.value);
      transactionP2PInfos.value = data;
      await loadP2PRelatedTransactions();
    } catch (error: unknown) {
      const responseStatus = (error as FetchError)?.response?.status;
      if (responseStatus === 404) {
        router.replace({
          name: 'partner-transactions-all',
          params: { partner: route.params.partner }
        });
      }
      notifyApiError(error, { title: t('common.transactionDetailsNotFound') });
    }
  };

  return {
    transactionId,
    transactionDetailsTabs,
    isBankTransferMoneyOut,
    isPspMethod,
    resetTransactionInfos,
    getTransactionBankAccountInfos,
    handleTransactionActionStatus,
    getTransactionInfos,
    getP2PTransactionInfos
  };
};
